import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { languages } from '../data/languages';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredLang, setHoveredLang] = useState<string | null>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  const handleLanguageSelect = (langId: string) => {
    navigate(`/merci/${langId}`);
  };

  return (
    <>
      <div 
        className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#001233] to-[#023E7D] text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 18, 51, 0.8), rgba(2, 62, 125, 0.9)), url('/src/assets/images/background.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Animated Particles Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 20 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: Math.random() * 50 + 10,
                height: Math.random() * 50 + 10,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, Math.random() * 100 - 50],
                x: [0, Math.random() * 100 - 50],
                opacity: [0.3, 0.7, 0.3],
                scale: [1, Math.random() * 0.5 + 1, 1]
              }}
              transition={{
                duration: Math.random() * 5 + 10,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>

        {/* Hero Section */}
        <div className="container mx-auto px-4 py-40 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-[#00AEEF] to-[#FFC0CB]">
                MultiLingua
              </h1>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <p className="text-xl md:text-2xl mb-12 text-gray-100">
                Découvrez la beauté des langues du monde et connectez-vous avec différentes cultures
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 max-w-3xl mx-auto"
            >
              {languages.map((lang) => (
                <motion.div
                  key={lang.id}
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
                    y: -5
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="relative flex flex-col items-center"
                  onMouseEnter={() => setHoveredLang(lang.id)}
                  onMouseLeave={() => setHoveredLang(null)}
                  onClick={() => handleLanguageSelect(lang.id)}
                >
                  <motion.div 
                    className="w-full p-4 rounded-xl cursor-pointer bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/40 transition-all duration-300"
                    style={{
                      background: hoveredLang === lang.id ? `linear-gradient(135deg, ${lang.color}40, transparent)` : 'rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <div className="text-4xl mb-2">{lang.flagEmoji}</div>
                    <p className="font-medium">{lang.name}</p>
                    <motion.p 
                      className="text-sm opacity-80 mt-1"
                      animate={{ opacity: hoveredLang === lang.id ? 1 : 0.8 }}
                    >
                      {lang.hello}
                    </motion.p>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* About Section */}
        <motion.div 
          id="about"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="bg-[#001233]/80 backdrop-blur-md py-20"
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold mb-8 text-center"
              >
                À propos de <span className="text-[#00AEEF]">MultiLingua</span>
              </motion.h2>
              
              <div className="grid md:grid-cols-2 gap-10 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-xl font-bold mb-3 text-[#00AEEF]">Notre Mission</h3>
                  <p className="text-gray-300 mb-6">
                    MultiLingua a pour mission de connecter les personnes à travers le monde en célébrant la diversité linguistique et culturelle. Nous croyons que chaque langue est une fenêtre vers une nouvelle perspective du monde.
                  </p>
                  
                  <h3 className="text-xl font-bold mb-3 text-[#00AEEF]">Notre Approche</h3>
                  <p className="text-gray-300">
                    Nous utilisons des technologies innovantes pour rendre l'apprentissage et la découverte des langues accessibles à tous, de manière ludique et interactive.
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.7 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-[#002D62] to-[#00AEEF] p-1">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <motion.div 
                        className="w-40 h-40 text-8xl flex items-center justify-center"
                        animate={{ 
                          rotate: 360,
                          scale: [1, 1.1, 1]
                        }}
                        transition={{
                          rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                          scale: { duration: 5, repeat: Infinity, ease: "easeInOut" }
                        }}
                      >
                        🌍
                      </motion.div>
                    </div>
                    
                    {/* Rotating language bubbles */}
                    {languages.slice(0, 5).map((lang, index) => (
                      <motion.div
                        key={lang.id}
                        className="absolute w-12 h-12 bg-white/90 backdrop-blur rounded-full flex items-center justify-center text-xl shadow-lg"
                        style={{ 
                          color: lang.color,
                        }}
                        animate={{
                          x: Math.cos(2 * Math.PI / 5 * index) * 120,
                          y: Math.sin(2 * Math.PI / 5 * index) * 120,
                        }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          repeatType: "reverse",
                          ease: "easeInOut",
                          delay: index * 0.5
                        }}
                      >
                        {lang.flagEmoji}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="py-20"
        >
          <div className="container mx-auto px-4">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-12 text-center"
            >
              Fonctionnalités <span className="text-[#00AEEF]">Principales</span>
            </motion.h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: "🌐",
                  title: "Multilingue",
                  description: "Découvrez plus de 10 langues différentes du monde entier, avec des traductions précises et culturellement pertinentes."
                },
                {
                  icon: "✨",
                  title: "Interface Interactive",
                  description: "Profitez d'une expérience utilisateur fluide et intuitive avec des animations et transitions élégantes."
                },
                {
                  icon: "🎓",
                  title: "Apprentissage Culturel",
                  description: "En plus des langues, découvrez des éléments culturels uniques associés à chaque région linguistique."
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/30 transition-all duration-300"
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold mb-3 text-[#00AEEF]">{feature.title}</h3>
                  <p className="text-gray-300">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default HomePage;
